/**
 * Retail Property Type Handler
 */

import { PropertyType } from '../../../shared/definitions/property';
import { CommercialPropertyHandler } from '../base/commercial-property-handler.abstract';
import { RoomTypeConfig, FeatureConfig, FeatureCategory } from '@core/services/property-analysis/property-room-config.service';

export class RetailHandler extends CommercialPropertyHandler {
  readonly propertyType = PropertyType.Retail;
  readonly displayName = 'Retail';
  readonly pluralName = 'Retail Properties';
  readonly icon = 'store';
  readonly description = 'Retail store or shop space';

  getRoomTypes(): RoomTypeConfig[] {
    return [
      {
        typeKey: 'sales_floor',
        label: 'Sales Floor',
        pluralLabel: 'Sales Floors',
        countField: 'salesFloors',
        icon: 'storefront',
        defaultCount: 0,
        description: 'Customer-facing sales area',
        formFields: [
          { name: 'name', label: 'Floor Name', type: 'text', placeholder: 'e.g., Ground Floor, Mezzanine' },
          { name: 'sqm', label: 'Sales Area', type: 'number', suffix: 'm²', required: true, min: 10 },
          { name: 'floorNumber', label: 'Floor Number', type: 'number', min: 0 },
          { name: 'hasDisplayWindows', label: 'Display Windows', type: 'checkbox', default: false },
          { name: 'numberOfDisplayWindows', label: 'Number of Display Windows', type: 'number', min: 0 },
          { name: 'shopFrontWidth', label: 'Shop Front Width', type: 'number', suffix: 'm', min: 2 },
          { name: 'ceilingHeight', label: 'Ceiling Height', type: 'number', suffix: 'm', min: 2.2 },
          { name: 'notes', label: 'Notes', type: 'textarea' }
        ]
      },
      {
        typeKey: 'storage_area',
        label: 'Storage Area',
        pluralLabel: 'Storage Areas',
        countField: 'storageAreas',
        icon: 'inventory',
        defaultCount: 0,
        description: 'Back-of-house storage space',
        formFields: [
          { name: 'name', label: 'Area Name', type: 'text', placeholder: 'e.g., Main Storage, Back Room' },
          { name: 'sqm', label: 'Storage Area', type: 'number', suffix: 'm²', required: true, min: 5 },
          { name: 'hasShelvingSystem', label: 'Shelving System', type: 'checkbox', default: false },
          { name: 'hasLoadingAccess', label: 'Loading Access', type: 'checkbox', default: false },
          { name: 'notes', label: 'Notes', type: 'textarea' }
        ]
      }
    ];
  }

  getFeatures(): FeatureConfig[] {
    const coreFeatures = this.getCoreCommercialFeatures();
    
    const retailSpecificFeatures: FeatureConfig[] = [
      {
        key: 'pos_systems',
        label: 'POS Systems',
        icon: 'point_of_sale',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Point of sale terminals and payment systems',
        detailsInterface: 'RetailTechnologyDetail',
        formFields: [
          { name: 'numberOfTerminals', label: 'Number of Terminals', type: 'number', required: true, min: 1 },
          { name: 'systemType', label: 'System Type', type: 'text', placeholder: 'e.g., Square, Lightspeed' },
          { name: 'hasInventoryManagement', label: 'Inventory Management', type: 'checkbox', default: false },
          { name: 'acceptsCards', label: 'Card Payments', type: 'checkbox', default: true },
          { name: 'acceptsContactless', label: 'Contactless Payments', type: 'checkbox', default: false }
        ]
      },
      {
        key: 'fitting_rooms',
        label: 'Fitting Rooms',
        icon: 'checkroom',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Customer fitting or changing rooms',
        detailsInterface: 'RetailTechnologyDetail',
        formFields: [
          { name: 'numberOfRooms', label: 'Number of Rooms', type: 'number', required: true, min: 1 },
          { name: 'hasLighting', label: 'Individual Lighting', type: 'checkbox', default: false },
          { name: 'hasMirrors', label: 'Full-Length Mirrors', type: 'checkbox', default: true }
        ]
      }
    ];

    return [...coreFeatures, ...retailSpecificFeatures];
  }

  override getMainCharacteristics(): string[] {
    return ['salesFloorArea', 'storageArea', 'shopFrontWidth', 'ceilingHeight'];
  }

  override getRecommendedMinSurface(): number {
    return 20;
  }

  override getPriceRangeMultiplier(): number {
    return 1.4;
  }
}

